import React, { useMemo, useState, useCallback } from 'react';
import { Upload, ChevronDown, ChevronRight, AlertTriangle, Copy, FileJson } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../../../components/ui/Button';
import { Textarea } from '../../../components/ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/Table';
import { Badge } from '../../../components/ui/Badge';
import { useAuth } from '../../../core/contexts/AuthContext';
import type { Client } from '../../../core/models/Client';
import type { TimeEntry } from '../../../core/models/TimeEntry';
import { TimeEntryStatus, TIME_ENTRY_TYPES } from '../../../core/models/TimeEntry';
import { UserRole } from '../../../core/models/User';
import {
  parseImportJson,
  normalizeImportRecord,
  findDuplicateMatch,
  diffTimeEntries,
  type DuplicateResolution,
  type NormalizedImport,
  type FieldDiff,
} from '../utils/timeEntryImport';

interface TimeEntryImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clients: Client[];
  existingEntries: TimeEntry[];
  createTimeEntry: (data: Omit<TimeEntry, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateTimeEntry: (entryId: string, updates: Partial<TimeEntry>) => Promise<void>;
  /** Uids allowed as import `userId` for client admins; for project admins should be only the current user. */
  allowedUserIds?: Set<string>;
}

interface PreviewLine {
  index: number;
  status: 'new' | 'duplicate' | 'error';
  errors: string[];
  normalized: NormalizedImport | null;
  existing?: TimeEntry;
  explicitId?: string;
  diff: FieldDiff[];
  resolution: DuplicateResolution;
  clientLabel: string;
}

function resolveClientLabel(clientId: string | undefined, clients: Client[]): string {
  if (!clientId) return 'Client general (org-level)';
  const c = clients.find((x) => x.id === clientId);
  if (!c) return `Unknown id ${clientId.slice(0, 10)}…`;
  const isProject =
    c.entityType === 'project' || c.entityType === 'project_legacy';
  return isProject ? `${c.name} (project)` : `${c.name} (client)`;
}

function entityKindLabel(c: Client): string {
  if (c.entityType === 'project' || c.entityType === 'project_legacy') return 'Project';
  if (c.entityType === 'client') return 'Client';
  return 'Project / client';
}

/** Parent client id for example rows when auth scope is empty (derived from first loaded project). */
function deriveExampleScopeId(defaultScopeId: string, clients: Client[]): string {
  if (defaultScopeId) return defaultScopeId;
  if (clients.length === 0) return '';
  const c0 = clients[0];
  if (c0.entityType === 'client') return c0.id;
  return (c0.clientId || c0.organizationId || '').trim();
}

function buildExampleEntries(
  defaultScopeId: string,
  clients: Client[],
  includeOrganizationIdOnRows: boolean,
  exampleUserId: string
): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  const scopeForExample = deriveExampleScopeId(defaultScopeId, clients);
  const uidExample = exampleUserId || 'YOUR_FIREBASE_AUTH_UID';
  const orgWide: Record<string, unknown> = {
    description: 'Example: client-wide time (no project on this row)',
    startTime: '2025-03-01T14:00:00.000Z',
    endTime: '2025-03-01T16:00:00.000Z',
    billable: true,
    type: 'Development',
    status: TimeEntryStatus.PENDING,
    userId: uidExample,
  };
  if (includeOrganizationIdOnRows && defaultScopeId) {
    orgWide.organizationId = defaultScopeId;
  } else if (scopeForExample) {
    orgWide.parentClientId = scopeForExample;
  }
  rows.push(orgWide);

  clients.forEach((c, i) => {
    const day = String(2 + (i % 27)).padStart(2, '0');
    const row: Record<string, unknown> = {
      description: `Example: time for ${c.name}`,
      startTime: `2025-03-${day}T10:00:00.000Z`,
      duration: 60,
      clientId: c.id,
      billable: true,
      type: 'Meeting',
      status: TimeEntryStatus.PENDING,
      userId: uidExample,
    };
    if (includeOrganizationIdOnRows && defaultScopeId) {
      row.organizationId = defaultScopeId;
    }
    rows.push(row);
  });
  return rows;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export const TimeEntryImportDialog: React.FC<TimeEntryImportDialogProps> = ({
  open,
  onOpenChange,
  clients,
  existingEntries,
  createTimeEntry,
  updateTimeEntry,
  allowedUserIds,
}) => {
  const { currentUser, organizationId, clientId: authClientId } = useAuth();
  const [jsonText, setJsonText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewLine[] | null>(null);
  const [defaultResolution, setDefaultResolution] = useState<DuplicateResolution>('ignore');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [exampleOpen, setExampleOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const allowPerRowOrg = currentUser?.role === UserRole.SUPER_ADMIN;

  /** Parent client / tenant scope (legacy field on documents: organizationId). */
  const defaultScopeId = organizationId || authClientId || '';

  const includeOrgIdInExampleRows = allowPerRowOrg && !!defaultScopeId;

  const idReferencePayload = useMemo(
    () => ({
      parentClientId: defaultScopeId || null,
      note:
        'Stored on each time entry as organizationId (legacy name). Set via your profile client, parentClientId on a row, or by resolving clientId/projectId against the project list below. Each row should include userId (Firebase Auth uid) for the person the time is attributed to unless it defaults to you.',
      clientWideTimeEntries: 'Omit project clientId/projectId, or set parentClientId if you have no profile client.',
      projectsAndClients: clients.map((c) => ({
        id: c.id,
        name: c.name,
        kind: entityKindLabel(c),
      })),
    }),
    [defaultScopeId, clients]
  );

  const exampleJsonString = useMemo(() => {
    const entries = buildExampleEntries(
      defaultScopeId,
      clients,
      includeOrgIdInExampleRows,
      currentUser?.uid || ''
    );
    return JSON.stringify({ entries }, null, 2);
  }, [defaultScopeId, clients, includeOrgIdInExampleRows, currentUser?.uid]);

  const idReferenceString = useMemo(
    () => JSON.stringify(idReferencePayload, null, 2),
    [idReferencePayload]
  );

  const copyWithFeedback = useCallback(async (key: string, text: string) => {
    const ok = await copyText(text);
    if (ok) {
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(null), 2000);
    } else {
      window.alert('Could not copy to clipboard.');
    }
  }, []);

  const buildPreview = useCallback(() => {
    setImportResult(null);
    setParseError(null);
    const parsed = parseImportJson(jsonText.trim());
    if (!parsed.ok) {
      setParseError(parsed.error);
      setPreview(null);
      return;
    }
    if (parsed.rows.length === 0) {
      setParseError('No records found in JSON.');
      setPreview(null);
      return;
    }

    const lines: PreviewLine[] = parsed.rows.map((raw, index) => {
      const explicitId = typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim() : undefined;
      const n = normalizeImportRecord(raw, {
        defaultScopeId,
        defaultUserId: currentUser?.uid || '',
        allowPerRowScopeOverride: allowPerRowOrg,
        clients,
        allowedUserIds: allowedUserIds ?? null,
        currentUserRole: currentUser?.role,
        currentUid: currentUser?.uid,
      });
      if (!n.ok || !n.value) {
        return {
          index,
          status: 'error' as const,
          errors: n.errors,
          normalized: null,
          diff: [],
          resolution: 'ignore' as DuplicateResolution,
          clientLabel: '—',
        };
      }
      const normalized = n.value;
      const clientLabel = resolveClientLabel(normalized.clientId, clients);
      const match = findDuplicateMatch(normalized, existingEntries, explicitId);
      if (match) {
        const diff = diffTimeEntries(match, normalized);
        return {
          index,
          status: 'duplicate' as const,
          errors: [],
          normalized,
          existing: match,
          explicitId,
          diff,
          resolution: defaultResolution,
          clientLabel,
        };
      }
      return {
        index,
        status: 'new' as const,
        errors: [],
        normalized,
        diff: [],
        resolution: 'ignore' as DuplicateResolution,
        clientLabel,
      };
    });
    setPreview(lines);
  }, [
    jsonText,
    defaultScopeId,
    currentUser?.uid,
    currentUser?.role,
    allowPerRowOrg,
    clients,
    existingEntries,
    defaultResolution,
    allowedUserIds,
  ]);

  const applyDefaultResolution = useCallback(() => {
    setPreview((prev) =>
      prev
        ? prev.map((line) =>
            line.status === 'duplicate' ? { ...line, resolution: defaultResolution } : line
          )
        : null
    );
  }, [defaultResolution]);

  const setLineResolution = (index: number, resolution: DuplicateResolution) => {
    setPreview((prev) =>
      prev
        ? prev.map((line) => (line.index === index ? { ...line, resolution } : line))
        : null
    );
  };

  const toggleExpand = (index: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const counts = useMemo(() => {
    if (!preview) return { new: 0, duplicate: 0, errors: 0, merge: 0, ignore: 0 };
    let newC = 0,
      dup = 0,
      err = 0,
      merge = 0,
      ign = 0;
    for (const p of preview) {
      if (p.status === 'error') err++;
      else if (p.status === 'new') newC++;
      else {
        dup++;
        if (p.resolution === 'merge') merge++;
        else ign++;
      }
    }
    return { new: newC, duplicate: dup, errors: err, merge, ignore: ign };
  }, [preview]);

  const runImport = async () => {
    if (!preview) return;
    const errors = preview.filter((p) => p.status === 'error');
    if (errors.length > 0) {
      setImportResult(`Fix ${errors.length} invalid row(s) before importing.`);
      return;
    }
    setImporting(true);
    setImportResult(null);
    let created = 0;
    let merged = 0;
    let skipped = 0;
    try {
      for (const line of preview) {
        if (line.status === 'new' && line.normalized) {
          await createTimeEntry(line.normalized);
          created++;
        } else if (line.status === 'duplicate' && line.existing && line.normalized) {
          if (line.resolution === 'ignore') {
            skipped++;
            continue;
          }
          await updateTimeEntry(line.existing.id, {
            description: line.normalized.description,
            startTime: line.normalized.startTime,
            endTime: line.normalized.endTime,
            duration: line.normalized.duration,
            billable: line.normalized.billable,
            status: line.normalized.status,
            clientId: line.normalized.clientId,
            type: line.normalized.type,
            contractId: line.normalized.contractId,
            serviceId: line.normalized.serviceId,
            userId: line.normalized.userId,
          });
          merged++;
        }
      }
      setImportResult(
        `Done: ${created} created, ${merged} merged, ${skipped} skipped (duplicates ignored).`
      );
    } catch (e) {
      console.error(e);
      setImportResult(`Import failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setImporting(false);
    }
  };

  const resetClose = () => {
    setJsonText('');
    setPreview(null);
    setParseError(null);
    setImportResult(null);
    setExpanded(new Set());
    setExampleOpen(false);
    setCopiedKey(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : resetClose())}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import time entries
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 flex-1 min-h-0 flex flex-col">
          <p className="text-sm text-gray-600">
            Paste JSON: an array of objects, or <code className="text-xs bg-gray-100 px-1 rounded">{'{ "entries": [...] }'}</code>.
            Each row needs <strong>description</strong>, <strong>startTime</strong>, and either <strong>endTime</strong> or{' '}
            <strong>duration</strong> (minutes). Optional: <code className="text-xs">clientId</code> or{' '}
            <code className="text-xs">projectId</code>, <code className="text-xs">type</code>, <code className="text-xs">billable</code>,{' '}
            <code className="text-xs">status</code>, <code className="text-xs">id</code> (match existing document).
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setExampleOpen((v) => !v)}
            >
              <FileJson className="h-4 w-4 mr-2" />
              {exampleOpen ? 'Hide example & schema' : 'Show example & schema'}
            </Button>
          </div>

          {exampleOpen && (
            <div className="border rounded-lg bg-gray-50 p-4 space-y-4 text-sm max-h-[min(480px,55vh)] overflow-y-auto">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Field reference</h4>
                <ul className="list-disc pl-5 space-y-1 text-gray-700 text-xs">
                  <li>
                    <code className="bg-white px-1 rounded">description</code> (string, required)
                  </li>
                  <li>
                    <code className="bg-white px-1 rounded">startTime</code> (ISO 8601 string, required)
                  </li>
                  <li>
                    <code className="bg-white px-1 rounded">endTime</code> (ISO string) or{' '}
                    <code className="bg-white px-1 rounded">duration</code> (minutes, number)
                  </li>
                  <li>
                    <code className="bg-white px-1 rounded">clientId</code> or{' '}
                    <code className="bg-white px-1 rounded">projectId</code> — project document id (from the list below); omit
                    both for client-wide time with no project
                  </li>
                  <li>
                    <code className="bg-white px-1 rounded">parentClientId</code> — parent client scope (stored as{' '}
                    <code className="bg-white px-1 rounded">organizationId</code> on the entry); use if your profile has no
                    client, or for explicit scope
                  </li>
                  <li>
                    <code className="bg-white px-1 rounded">organizationId</code> — same as parent client scope (optional;
                    super admin per-row)
                  </li>
                  <li>
                    <code className="bg-white px-1 rounded">userId</code> — Firebase Auth uid of the person the time is
                    attributed to (optional; defaults to you). Super admins may use any uid; client admins only uids in
                    their org; project admins must use their own uid.
                  </li>
                  <li>
                    <code className="bg-white px-1 rounded">type</code>: {TIME_ENTRY_TYPES.join(', ')}
                  </li>
                  <li>
                    <code className="bg-white px-1 rounded">status</code>:{' '}
                    {Object.values(TimeEntryStatus).join(', ')}
                  </li>
                  <li>
                    <code className="bg-white px-1 rounded">billable</code> (boolean),{' '}
                    <code className="bg-white px-1 rounded">contractId</code>, <code className="bg-white px-1 rounded">serviceId</code>,{' '}
                    <code className="bg-white px-1 rounded">id</code> (existing time entry doc id for merge)
                  </li>
                </ul>
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <h4 className="font-semibold text-gray-900">Client & project ids</h4>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyWithFeedback('ids', idReferenceString)}
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" />
                    {copiedKey === 'ids' ? 'Copied' : 'Copy ID map (JSON)'}
                  </Button>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  Current parent client scope from your profile (also importable as{' '}
                  <code className="bg-white px-1 rounded">parentClientId</code>)
                  {defaultScopeId ? (
                    <>
                      : <code className="bg-white px-1 rounded text-xs break-all">{defaultScopeId}</code>
                    </>
                  ) : (
                    ' — not set. Use parentClientId on a row, or a project clientId we can resolve from the list below.'
                  )}
                </p>
                <div className="border rounded bg-white overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Kind</TableHead>
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">Firestore id</TableHead>
                        <TableHead className="text-xs w-[88px]">Copy</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="text-xs">Client-wide time</TableCell>
                        <TableCell className="text-xs text-gray-600">No project</TableCell>
                        <TableCell className="text-xs font-mono text-gray-500">Omit clientId / projectId</TableCell>
                        <TableCell className="text-xs">—</TableCell>
                      </TableRow>
                      {clients.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="text-xs">
                            <Badge variant="outline" className="font-normal">
                              {entityKindLabel(c)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs max-w-[140px] truncate" title={c.name}>
                            {c.name}
                          </TableCell>
                          <TableCell className="text-xs font-mono break-all">{c.id}</TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => copyWithFeedback(`id-${c.id}`, c.id)}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {clients.length === 0 && (
                  <p className="text-xs text-amber-800 mt-2">
                    No projects loaded for this organization. You can still import org-wide rows (no{' '}
                    <code className="bg-white px-1 rounded">clientId</code>).
                  </p>
                )}
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <h4 className="font-semibold text-gray-900">Example import JSON</h4>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => copyWithFeedback('example', exampleJsonString)}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1" />
                      {copiedKey === 'example' ? 'Copied' : 'Copy example'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setJsonText(exampleJsonString);
                        setPreview(null);
                        setParseError(null);
                        setImportResult(null);
                      }}
                    >
                      Load into editor
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  Includes one org-wide row and one sample row per project above. Remove or edit rows before importing.
                </p>
                <textarea
                  readOnly
                  value={exampleJsonString}
                  rows={Math.min(18, 6 + clients.length * 3)}
                  className="w-full font-mono text-xs rounded-md border border-gray-200 bg-white px-3 py-2"
                  spellCheck={false}
                />
              </div>
            </div>
          )}

          <Textarea
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value);
              setPreview(null);
              setParseError(null);
              setImportResult(null);
            }}
            placeholder={`[\n  {\n    "description": "Feature work",\n    "startTime": "2025-03-01T14:00:00.000Z",\n    "endTime": "2025-03-01T16:00:00.000Z",\n    "clientId": "optional-firestore-project-id",\n    "type": "Development",\n    "billable": true\n  }\n]`}
            rows={8}
            className="font-mono text-xs"
          />

          {parseError && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {parseError}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="secondary" onClick={buildPreview}>
              Dry run (preview)
            </Button>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Default for duplicates:</span>
              <Select
                value={defaultResolution}
                onValueChange={(v) => setDefaultResolution(v as DuplicateResolution)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ignore">Ignore (skip)</SelectItem>
                  <SelectItem value="merge">Merge (overwrite)</SelectItem>
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" onClick={applyDefaultResolution}>
                Apply to all duplicates
              </Button>
            </div>
          </div>

          {preview && preview.length > 0 && (
            <div className="border rounded-lg flex flex-col flex-1 min-h-0">
              <div className="px-3 py-2 border-b bg-gray-50 text-sm flex flex-wrap gap-2">
                <Badge variant="default">{counts.new} new</Badge>
                <Badge variant="warning">{counts.duplicate} duplicate</Badge>
                {counts.merge > 0 && <Badge variant="default">{counts.merge} merge</Badge>}
                {counts.ignore > 0 && <Badge variant="default">{counts.ignore} ignore</Badge>}
                {counts.errors > 0 && <Badge variant="error">{counts.errors} invalid</Badge>}
              </div>
              <div className="overflow-auto max-h-[min(420px,50vh)]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8" />
                      <TableHead>#</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Client / project</TableHead>
                      <TableHead>When</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Duplicate action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((line) => (
                      <React.Fragment key={line.index}>
                        <TableRow
                          className={
                            line.status === 'error'
                              ? 'bg-red-50'
                              : line.status === 'duplicate'
                                ? 'bg-amber-50/80'
                                : undefined
                          }
                        >
                          <TableCell className="p-1">
                            {line.status === 'duplicate' && line.diff.length > 0 && (
                              <button
                                type="button"
                                className="p-1 rounded hover:bg-gray-200"
                                onClick={() => toggleExpand(line.index)}
                                aria-label="Toggle diff"
                              >
                                {expanded.has(line.index) ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </button>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{line.index + 1}</TableCell>
                          <TableCell>
                            {line.status === 'new' && <Badge variant="success">New</Badge>}
                            {line.status === 'duplicate' && <Badge variant="warning">Duplicate</Badge>}
                            {line.status === 'error' && <Badge variant="error">Invalid</Badge>}
                          </TableCell>
                          <TableCell className="max-w-[140px] truncate text-xs" title={line.clientLabel}>
                            {line.clientLabel}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            {line.normalized
                              ? format(line.normalized.startTime, 'yyyy-MM-dd HH:mm')
                              : '—'}
                          </TableCell>
                          <TableCell className="text-xs">
                            {line.normalized ? `${line.normalized.duration} min` : '—'}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate text-xs" title={line.normalized?.description}>
                            {line.status === 'error' ? (
                              <span className="text-red-700">{line.errors.join('; ')}</span>
                            ) : (
                              line.normalized?.description ?? '—'
                            )}
                          </TableCell>
                          <TableCell>
                            {line.status === 'duplicate' ? (
                              <Select
                                value={line.resolution}
                                onValueChange={(v) =>
                                  setLineResolution(line.index, v as DuplicateResolution)
                                }
                              >
                                <SelectTrigger className="h-8 w-[130px] text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ignore">Ignore</SelectItem>
                                  <SelectItem value="merge">Merge</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                        {line.status === 'duplicate' && expanded.has(line.index) && line.existing && line.normalized && (
                          <TableRow className="bg-gray-50">
                            <TableCell colSpan={8} className="p-3">
                              <p className="text-xs font-medium text-gray-700 mb-2">
                                Diff — existing <code className="bg-white px-1 rounded">{line.existing.id}</code> vs import
                              </p>
                              {line.diff.length === 0 ? (
                                <p className="text-xs text-gray-500">No field differences (still matched as duplicate by fingerprint).</p>
                              ) : (
                                <table className="w-full text-xs border-collapse">
                                  <thead>
                                    <tr className="text-left text-gray-500">
                                      <th className="p-1 pr-3">Field</th>
                                      <th className="p-1 pr-3">Existing</th>
                                      <th className="p-1">Import</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {line.diff.map((d) => (
                                      <tr key={d.field} className="border-t border-gray-200">
                                        <td className="p-1 font-mono text-gray-700">{d.field}</td>
                                        <td className="p-1 text-red-800 line-through decoration-red-400/60 max-w-[200px] break-all">
                                          {d.before}
                                        </td>
                                        <td className="p-1 text-green-800 max-w-[200px] break-all">{d.after}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {importResult && (
            <p className="text-sm text-gray-800 bg-gray-100 border rounded px-3 py-2">{importResult}</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={resetClose}>
            Close
          </Button>
          <Button
            type="button"
            loading={importing}
            disabled={!preview || preview.some((p) => p.status === 'error')}
            onClick={runImport}
          >
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
